import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import styled from "styled-components";

const CustomPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    color: "#000000",
    borderColor: "#fff",
    backgroundColor: "#fff",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    background: "linear-gradient(90deg,hsl(248, 77.20%, 70.80%), #244C77)",
    color: "#FFFFFF",
  },
  "& .MuiPaginationItem-root:hover": {
    background: "linear-gradient(90deg,rgb(73, 63, 143),rgb(48, 33, 150))",
    color: "#FFFFFF",
  },
}));

const getButtonStyle = (status) => {
  switch (status) {
    case "Received":
      return { ...styles.button, ...styles.receivedButton };
    case "In-Progress":
      return { ...styles.button, ...styles.inProgressButton };
    case "Accept":
      return { ...styles.button, ...styles.acceptButton };
    case "Reject":
      return { ...styles.button, ...styles.rejectButton };
    default:
      return styles.button;
  }
};

const getButtonIcon = (status) => {
  switch (status) {
    case "Accept":
      return <CheckCircleIcon style={{ marginRight: "5px" }} />;
    case "Reject":
      return <HighlightOffIcon style={{ marginRight: "5px" }} />;
    default:
      return null;
  }
};

const styles = {
  container: {
    padding: "20px",
    backgroundColor: "transparent",
  },
  tableContainer: {
    backgroundColor: "transparent",
    borderRadius: "8px",
    overflowX: "auto",
    minHeight: "500px",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: "0 8px",
    width: "100%",
  },
  tableHeader: {
    backgroundColor: "#8F95B2",
  },
  headerRow: {
    backgroundColor: "#8F95B2",
    borderRadius: "8px 8px 0 0",
  },
  headerText: {
    fontWeight: "bold",
    color: "white",
    padding: "12px",
    borderBottom: "none",
    whiteSpace: "nowrap",
  },
  button: {
    fontWeight: "bold",
    fontSize: "12px",
    textTransform: "capitalize",
    padding: "5px 10px",
  },
  receivedButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(117, 193, 55, 0.34)",
    color: "#75C137",
  },
  inProgressButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(35, 144, 255, 0.34)",
    color: "#2390FF",
  },
  acceptButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(117, 193, 55, 0.34)",
    color: "#75C137",
  },
  rejectButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(242, 83, 52, 0.34)",
    color: "#F25334",
  },
  pagination: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "center",
  },
};

const getHeaderStickyStyles = (index, totalColumns) => {
  if (totalColumns < 3) return {};

  const isStickyLeft1 = index === 0;
  const isStickyLeft2 = index === 1;
  const isStickyRight = index === totalColumns - 1;

  if (isStickyLeft1) {
    return {
      position: "sticky",
      left: 0,
      zIndex: 3,
      backgroundColor: "#8F95B2",
      width: "120px",
      minWidth: "120px",
      maxWidth: "120px",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
    };
  }
  if (isStickyLeft2) {
    return {
      position: "sticky",
      left: "120px",
      zIndex: 3,
      backgroundColor: "#8F95B2",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
    };
  }
  if (isStickyRight) {
    return {
      position: "sticky",
      right: 0,
      zIndex: 3,
      backgroundColor: "#8F95B2",
      width: "140px",
      minWidth: "140px",
      maxWidth: "140px",
      boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)",
    };
  }
  return {};
};

const getBodyStickyStyles = (index, totalColumns) => {
  if (totalColumns < 3) return { backgroundColor: "#ffffff" };

  const isStickyLeft1 = index === 0;
  const isStickyLeft2 = index === 1;
  const isStickyRight = index === totalColumns - 1;

  if (isStickyLeft1) {
    return {
      position: "sticky",
      left: 0,
      zIndex: 1,
      backgroundColor: "#ffffff",
      width: "120px",
      minWidth: "120px",
      maxWidth: "120px",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
    };
  }
  if (isStickyLeft2) {
    return {
      position: "sticky",
      left: "120px",
      zIndex: 1,
      backgroundColor: "#ffffff",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
    };
  }
  if (isStickyRight) {
    return {
      position: "sticky",
      right: 0,
      zIndex: 1,
      backgroundColor: "#ffffff",
      width: "140px",
      minWidth: "140px",
      maxWidth: "140px",
      boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.1)",
    };
  }
  return {
    backgroundColor: "#ffffff",
  };
};

const Dynamictablepagination = ({ columns = [], rows = [], page = 1, totalPages = 1, handleChangePage }) => {
  return (
    <Box style={styles.container}>
      <TableContainer style={styles.tableContainer}>
        <Table style={styles.table}>
          <TableHead>
            <TableRow style={styles.headerRow}>
              {columns?.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    ...styles.headerText,
                    borderRadius:
                      index === 0
                        ? "8px 0 0 0"
                        : index === columns.length - 1
                        ? "0 8px 0 0"
                        : "0",
                    ...getHeaderStickyStyles(index, columns.length)
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  position: "relative",
                  overflow: "hidden", // Ensure the border-radius effect is visible
                  cursor: "pointer",
                  "&:hover td": {
                    backgroundColor: "#DAD8FC",
                  },
                  "&:hover td:first-of-type": {
                    borderTopLeftRadius: "10px",
                    borderBottomLeftRadius: "10px",
                  },
                  "&:hover td:last-of-type": {
                    borderTopRightRadius: "10px",
                    borderBottomRightRadius: "10px",
                  },
                }}
              >
                {columns?.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      border: "none",
                      color: "#8F95B2",
                      whiteSpace: "nowrap",
                      ...getBodyStickyStyles(colIndex, columns.length)
                    }}
                  >
                    {column.field === "status" ? (
                      <Button
                        style={getButtonStyle(row[column.field])}
                        startIcon={getButtonIcon(row[column.field])}
                      >
                        {row[column.field]}
                      </Button>
                    ) : (
                      row[column.field]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box style={styles.pagination}>
        <CustomPagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          variant="outlined"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Dynamictablepagination;
